import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";
import { RiDeleteBin5Line, RiDeleteBin6Line } from "react-icons/ri";
import { FaArrowRight } from "react-icons/fa";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { _imagesURL } from "../../constants/API";
import { Language } from "../../App";
import { IoClose } from "react-icons/io5";
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
// Same PayPal (Braintree Drop-in) + Klarna (Stripe embedded Checkout Session)
// integration used for course purchases — see
// src/components/course-details/PaypalPaymentPage.jsx for the reference flow.
import { fetchPaypalToken, generateNonceWithDropIn } from "../../services/paypalApi";
import { createKlarnaCheckoutSession, getKlarnaCheckoutSessionStatus } from "../../services/stripeApi";
import { CheckoutProvider } from "../payments/klarna-stripe/CheckoutProvider";
import { CheckoutForm } from "../payments/klarna-stripe/CheckoutForm";
import { SessionStatus } from "../payments/klarna-stripe/SessionStatus";

// Same endpoint ApplyMentorShip.jsx uses — user_id is the mentor's id, and
// the actual profile is nested one level deeper under data.data.mentor.
async function fetchMentorProfile(userId) {
  const res = await apiAdmin.post("user/mentor/profile", { user_id: userId });
  return res.data;
}

// Used only if SplashData hasn't loaded into localStorage yet.
const FALLBACK_ACCOUNT_DETAILS = {
  fullName: "Dr. Jane Smith",
  workEmail: "janesmith@hospital.com",
  image: placeholderAvatar,
};

// Same SplashData.user source CreateMentorship.jsx reads from — the
// currently logged-in doctor applying, not the mentor being applied to.
function getAccountDetails() {
  try {
    const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
    if (!user) return FALLBACK_ACCOUNT_DETAILS;
    return {
      fullName:
        user.name ||
        [user.firstname, user.lastname].filter(Boolean).join(" ") ||
        FALLBACK_ACCOUNT_DETAILS.fullName,
      workEmail: user.email || FALLBACK_ACCOUNT_DETAILS.workEmail,
      image: user.image
        ? `${_imagesURL}${user.image}`
        : FALLBACK_ACCOUNT_DETAILS.image,
    };
  } catch (e) {
    return FALLBACK_ACCOUNT_DETAILS;
  }
}

// How long an upload can run before we switch from the normal "Uploading…"
// indicator to the "Upload Delay" state (per Figma: taking longer than
// expected / stuck in progress for an extended period).
const UPLOAD_DELAY_THRESHOLD_MS = 6000;

function CheckSvg() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path
        d="M1 4L3.5 6.5L9 1"
        stroke="#00BADA"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Small green circular checkmark badge — pinned to the top-right corner of
// whichever payment option is currently selected, overlapping the option's
// own border/edge like a "selected" pin.
function SelectedBadge() {
  return (
    <span
      className="d-flex align-items-center justify-content-center"
      style={{
        position: "absolute",
        top: "-9px",
        right: "0px",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: "#34C759",
        border: "1.5px solid #0612234D",
        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      }}
    >
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="#111"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

// Rounded file/document icon — light green pill background, matches the
// "uploaded" card look from the design.
function CvFileIcon() {
  return (
    <div
      style={{
        width: "38px",
        height: "38px",
        borderRadius: "10px",
        background: "#E6F7EE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6Z"
          stroke="#1F9D55"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M13 2v5a1 1 0 0 0 1 1h5"
          stroke="#1F9D55"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Same rounded slot, but amber-tinted — used for the "delayed" state so it
// reads as a soft warning rather than a normal in-progress spinner.
function CvDelayedIcon() {
  return (
    <div
      style={{
        width: "38px",
        height: "38px",
        borderRadius: "10px",
        background: "#FEF3E2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#B45309" strokeWidth="1.7" />
        <path
          d="M12 7v5l3 2"
          stroke="#B45309"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Small rotating ring — used inside both the uploading and delayed cards.
function SpinnerIcon({ color = "#5F6673", size = 14 }) {
  return (
    <>
      <style>{`@keyframes cvUploadSpin{to{transform:rotate(360deg)}}`}</style>
      <span
        style={{
          display: "inline-block",
          width: `${size}px`,
          height: `${size}px`,
          border: `2px solid ${color}33`,
          borderTopColor: color,
          borderRadius: "50%",
          animation: "cvUploadSpin 0.7s linear infinite",
          flexShrink: 0,
        }}
      />
    </>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"
        stroke="#E24C4C"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke="#5F6673"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

// "Uploaded just now" / "Uploaded 4 mins ago" / "Uploaded 2 hours ago" —
// recomputed live off uploadedAt so it never goes stale while the modal
// stays open. Builds the "X ago" part via mentorship_time_ago_template
// (same as ApplyMentorShip.jsx's formatElapsedSince) since Spanish puts
// "hace" before the value instead of after like English "ago" — the number
// + unit part alone doesn't reorder between languages, only that wrapper.
function formatUploadedAgo(uploadedAt, lang) {
  if (!uploadedAt) return "";
  const seconds = Math.floor((Date.now() - uploadedAt) / 1000);
  const prefixTemplate =
    lang?.mentorship_uploaded_time_template || "Uploaded {{time}}";
  let timeStr;
  if (seconds < 45) {
    timeStr = lang?.just_now || "Just now";
  } else {
    const minutes = Math.floor(seconds / 60);
    let duration;
    if (minutes < 60) {
      duration = `${minutes} ${minutes > 1 ? lang?.mins || "mins" : lang?.min || "min"}`;
    } else {
      const hours = Math.floor(minutes / 60);
      if (hours < 24) {
        duration = `${hours} ${hours > 1 ? lang?.hours || "hours" : lang?.hour || "hour"}`;
      } else {
        const days = Math.floor(hours / 24);
        duration = `${days} ${days > 1 ? lang?.days || "days" : lang?.day || "day"}`;
      }
    }
    const agoTemplate = lang?.mentorship_time_ago_template || "{{time}} ago";
    timeStr = agoTemplate.replace("{{time}}", duration);
  }
  return prefixTemplate.replace("{{time}}", timeStr);
}

function mentorSummary(mentor) {
  if (!mentor) return "";
  return [mentor.specialty, mentor.institution, mentor.institutionSub]
    .filter(Boolean)
    .join(" · ");
}

// The dropzone's <input accept=".pdf"> only filters what the OS file picker
// shows — the user can still switch it to "All files" and pick anything, and
// drag-and-drop (handleDrop below) never goes through the input at all, so
// it isn't filtered by `accept` even in the picker case. This is the actual
// gate: checked before a file is ever handed to startUpload.
function isPdfFile(file) {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

function MentorshipApplicationModal({ isOpen, toggle, mentor, onSubmitted }) {
  const { lang } = useContext(Language);
  const [checked1, setChecked1] = useState(false);
  const [checked2, setChecked2] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("paypal");
  const [dragOver, setDragOver] = useState(false);
  const [mentorProfileData, setMentorProfileData] = useState(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  // Set true the first time Continue is clicked with something missing —
  // flips on the CV dropzone border / checkbox border to red and reveals
  // their inline error text. Stays true afterwards so both errors clear
  // themselves live as soon as the user fixes each one, without needing
  // another click.
  const [showValidation, setShowValidation] = useState(false);
  // Payment step — mirrors PaypalPaymentPage.jsx's Braintree Drop-in /
  // Stripe embedded Checkout Session flow. The mentor/request API call
  // itself only fires once one of these succeeds — see submitMentorRequest.
  const [processingPayment, setProcessingPayment] = useState(false);
  const [dropInModal, setDropInModal] = useState(false);
  const [klarnaModal, setKlarnaModal] = useState(false);
  const [klarnaClientSecret, setKlarnaClientSecret] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [sessionPaymentStatus, setSessionPaymentStatus] = useState(null);
  const accountDetails = getAccountDetails();

  useEffect(() => {
    if (!isOpen || !mentor?.id) {
      setMentorProfileData(null);
      return;
    }
    let cancelled = false;
    fetchMentorProfile(mentor.id)
      .then((data) => {
        if (!cancelled && data?.status)
          setMentorProfileData(data.data?.mentor || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, mentor?.id]);

  // Real "Receive CV with Mentorship Requests" setting from this specific
  // mentor's profile — the CV section only shows when they've turned it on.
  const mentorProfile = mentorProfileData?.mentor_profile;
  const showCvUpload = mentorProfile?.is_receive_data === 1;

  // The `mentor` prop is just whatever the list card already had (often
  // missing specialty/institution) — once the real profile lands, prefer
  // its specialty/institute so the "SELECTED MENTOR" card actually shows
  // both, same fields ApplyMentorShip.jsx's own `display` object prefers.
  const mentorDisplayInfo = {
    ...mentor,
    specialty:
      mentorProfile?.specialty ||
      mentorProfileData?.category ||
      mentorProfileData?.subcategory ||
      mentor?.specialty,
    institution: mentorProfile?.institute || mentor?.institution,
  };
  const cvDropzoneText =
    showCvUpload && mentorProfile?.receive_data_title
      ? mentorProfile.receive_data_title
      : null;

  const [showBookedModal, setShowBookedModal] = useState(false);

  const [cvFile, setCvFile] = useState(null);
  // "idle" -> no file yet, dropzone visible
  // "uploading" -> file picked, upload in flight, within normal time window
  // "delayed" -> still uploading past UPLOAD_DELAY_THRESHOLD_MS
  // "uploaded" -> upload finished, file card with delete/replace shown
  const [cvUploadState, setCvUploadState] = useState("idle");
  const [uploadedAt, setUploadedAt] = useState(null);
  // Forces a re-render every 30s so "Uploaded just now" keeps advancing
  // ("1 min ago", "2 mins ago"...) without needing any other state change.
  const [, forceTick] = useState(0);

  const delayTimerRef = useRef(null);
  const uploadTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);

  const clearUploadTimers = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (uploadTimerRef.current) {
      clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearUploadTimers();
  }, []);

  // Keep the relative "Uploaded X ago" label fresh while a file sits uploaded.
  useEffect(() => {
    if (cvUploadState !== "uploaded") return;
    tickIntervalRef.current = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(tickIntervalRef.current);
  }, [cvUploadState]);

  // Placeholder for the real upload call — swap this out for the actual
  // API request. Kept as its own function so wiring up a real endpoint
  // later is a one-line change; everything else (delay detection, state
  // transitions, cleanup) stays the same.
  const uploadCvFile = (file) => {
    return new Promise((resolve) => {
      const duration = 1500 + Math.random() * 2000;
      uploadTimerRef.current = setTimeout(resolve, duration);
    });
  };

  const startUpload = (file) => {
    if (!file) return;
    clearUploadTimers();
    setCvFile(file);
    setCvUploadState("uploading");
    setUploadedAt(null);

    // Flip to the "delayed" visual if the upload is still going after the
    // threshold — this only changes what's shown, the upload itself keeps
    // running underneath.
    delayTimerRef.current = setTimeout(() => {
      setCvUploadState((prev) => (prev === "uploading" ? "delayed" : prev));
    }, UPLOAD_DELAY_THRESHOLD_MS);

    uploadCvFile(file).then(() => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setCvUploadState("uploaded");
      setUploadedAt(Date.now());
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    // Reset the input so selecting the same filename again after a delete
    // still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error(lang?.please_select_pdf_file || "Please select pdf file*");
      return;
    }
    startUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (cvUploadState !== "idle") return; // a file is already in flight/uploaded
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error(lang?.please_select_pdf_file || "Please select pdf file*");
      return;
    }
    startUpload(file);
  };

  // Cancels an in-flight upload (uploading/delayed) — back to the empty
  // dropzone, nothing kept.
  const handleCancelUpload = () => {
    clearUploadTimers();
    setCvFile(null);
    setCvUploadState("idle");
    setUploadedAt(null);
  };

  // Removes a completed upload — this is the only way back to the empty
  // dropzone once a file has finished uploading, so a new PDF can't be
  // picked while one is already attached.
  const handleDeleteCv = () => {
    clearUploadTimers();
    setCvFile(null);
    setCvUploadState("idle");
    setUploadedAt(null);
  };

  // Same curtain-modal pattern as ApplyMentorShip.jsx / NewCourseDetail2.0.jsx's
  // VideoModal — plain backdrop + content divs animated with @keyframes,
  // not reactstrap's Modal/Fade, so open and close are both immediate and
  // symmetric instead of gated behind a JS timeout. Two independent
  // isClosing flags because this component shows two separate modals in
  // sequence (the application form, then the booked confirmation).
  const [isClosing, setIsClosing] = useState(false);
  const [isBookedClosing, setIsBookedClosing] = useState(false);

  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  const closeBookedModal = () => {
    setIsBookedClosing(true);
    setTimeout(() => {
      setShowBookedModal(false);
      setIsBookedClosing(false);
    }, 800);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (showBookedModal) closeBookedModal();
      else if (isOpen) handleClose();
    };
    if (isOpen || showBookedModal) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showBookedModal]);

  // Klarna's checkout can redirect the browser away and back (e.g. its own
  // financing approval step) — this picks the flow back up on return via
  // ?session_id= in the URL, same pattern as PaypalPaymentPage.jsx. Must sit
  // above the `if (!mentor) return null` below — hooks can't be conditional.
  // Note: the CV File object doesn't survive the redirect/reload, so a
  // request completed this way never carries a CV even if one was attached
  // before Klarna redirected away.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const pending = localStorage.getItem("mam_pending_request");
    if (!sessionId || !pending || !mentor?.id) return;

    setProcessingPayment(true);
    getKlarnaCheckoutSessionStatus(sessionId)
      .then((res) => {
        const sessionData = res?.data?.data ?? res?.data;
        const paymentStatus = sessionData?.payment_status ?? res?.payment_status;
        setSessionPaymentStatus(paymentStatus);
        if (paymentStatus === "paid") {
          return submitMentorRequest({ paymentType: "stripe", nonce: sessionId });
        }
        throw new Error(`Payment was not completed (status: ${paymentStatus ?? "unknown"}).`);
      })
      .catch((err) => {
        console.error("Klarna session verification error:", err);
        setSessionError(err?.response?.data?.message || err.message || (lang?.payment_verify_failed || "Failed to verify payment."));
        setProcessingPayment(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentor?.id]);

  if (!mentor) return null;

  // The mentor's own asking price, set on their application form
  // (CreateMentorship.jsx's "price" field).
  const mentorPrice = Number(mentorProfile?.price ?? mentor.mentor_profile?.price ?? 0) || 0;

  // Only actually required when this mentor even asked for a CV; a finished
  // upload ("uploaded") is the only state that counts — still
  // uploading/delayed doesn't, so Continue stays blocked until it lands.
  const cvMissing = showCvUpload && cvUploadState !== "uploaded";
  const checkboxMissing = !checked1;

  // Actually submits the mentor/request — only ever called once a payment
  // has actually gone through (paymentMeta.nonce set), or the mentorship is
  // free (mentorPrice is 0) — never straight off the Continue click.
  const submitMentorRequest = async (paymentMeta) => {
    setSubmittingRequest(true);
    try {
      const fd = new FormData();
      fd.append("mentor_id", mentor.id);
      fd.append("mentorship_type", "One-To-One");
      // Only attached if the CV upload actually finished — not while
      // idle/uploading/delayed.
      if (cvUploadState === "uploaded" && cvFile) {
        fd.append("data", cvFile);
      }
      if (paymentMeta?.paymentType) fd.append("payment_type", paymentMeta.paymentType);
      if (paymentMeta?.nonce) fd.append("nonce", paymentMeta.nonce);
      const res = await apiAdmin.post("user/mentor/request", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.status) {
        localStorage.removeItem("mam_pending_request");
        onSubmitted?.(mentor);
        setDropInModal(false);
        setKlarnaModal(false);
        closeThen(() => setShowBookedModal(true));
      } else {
        toast.error(
          getApiErrorMessage(
            res.data,
            lang?.mentorship_error_submit_request ||
              "Couldn't submit your request. Please try again.",
          ),
        );
      }
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err.response?.data,
          lang?.mentorship_error_submit_request ||
            "Couldn't submit your request. Please try again.",
        ),
      );
    } finally {
      setSubmittingRequest(false);
      setProcessingPayment(false);
    }
  };

  // ── PayPal / Card — Braintree Drop-in, same as PaypalPaymentPage.jsx ──────
  const handlePaypalPayment = async () => {
    setProcessingPayment(true);
    try {
      if (mentorPrice > 0) {
        const userToken = await fetchPaypalToken();
        setDropInModal(true);
        const nonce = await generateNonceWithDropIn(userToken, "mam-dropin-container", mentorPrice.toFixed(2));
        await submitMentorRequest({ paymentType: "paypal", nonce });
      } else {
        // Free mentorship (no price set) — nothing to tokenize.
        await submitMentorRequest({ paymentType: "paypal" });
      }
    } catch (err) {
      console.error("PayPal payment error:", err);
      toast.error(lang?.mentorship_paypal_payment_error || "Couldn't process PayPal payment. Please try again.");
      setDropInModal(false);
      setProcessingPayment(false);
    }
  };

  // ── Klarna — Stripe embedded Checkout Session, same as PaypalPaymentPage.jsx ──
  const handleKlarnaPayment = async () => {
    setProcessingPayment(true);
    setSessionError(null);
    try {
      if (mentorPrice > 0) {
        // Persisted so the redirect-return effect above can finish this
        // request even after Klarna sends the browser away and back.
        localStorage.setItem("mam_pending_request", JSON.stringify({ mentorId: mentor.id }));
        const amount = Math.round(mentorPrice * 100);
        const response = await createKlarnaCheckoutSession({
          courseId: mentor.id,
          amount,
          title: `Mentorship with ${mentor.name}`,
        });
        const clientSecret = response?.data?.client_secret ?? response?.client_secret;
        if (clientSecret) {
          setKlarnaClientSecret(clientSecret);
          setKlarnaModal(true);
        } else {
          throw new Error(lang?.klarna_init_error || "Could not initialize Klarna checkout.");
        }
      } else {
        // Free mentorship (no price set) — nothing to check out.
        await submitMentorRequest({ paymentType: "stripe" });
      }
    } catch (err) {
      console.error("Klarna payment error:", err);
      toast.error(lang?.mentorship_klarna_payment_error || "Couldn't start Klarna checkout. Please try again.");
      setProcessingPayment(false);
    }
  };

  // Continue no longer submits the request directly — it only kicks off
  // whichever payment flow is selected, and that flow's own success handler
  // (submitMentorRequest, above) is what actually applies. Apply can never
  // fire ahead of a completed payment.
  const handleContinue = () => {
    if (cvMissing || checkboxMissing) {
      setShowValidation(true);
      return;
    }
    if (selectedPayment === "klarna") {
      handleKlarnaPayment();
    } else {
      handlePaypalPayment();
    }
  };

  return (
    <>
      {/* Curtain animation shared by both modals below — same rules
        ApplyMentorShip.jsx uses, just under this file's own mam-/mbk-
        prefixes so nothing collides if both ever render on the same page. */}
      <style>{`
      .mam-curtain-backdrop {
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1050;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        animation: mamBackdropFadeIn 0.8s ease-out forwards;
      }
      .mam-curtain-backdrop.closing {
        animation: mamBackdropFadeOut 0.8s ease-out forwards;
      }
      .mam-curtain-content {
        position: relative;
        width: 100%;
        max-width: 540px;
        max-height: 90vh;
        overflow-y: auto;
        background: #fff;
        border-radius: 18px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        transform: translateY(-100vh);
        animation: mamCurtainOpen 0.8s ease-out forwards;
      }
      .mbk-curtain-content {
        position: relative;
        width: 100%;
        max-width: 520px;
        max-height: 90vh;
        overflow-y: auto;
        background: #fff;
        border-radius: 20px;
        box-shadow: 0 24px 64px rgba(0,0,0,0.25);
        transform: translateY(-100vh);
        animation: mamCurtainOpen 0.8s ease-out forwards;
      }
      .mam-curtain-content.closing,
      .mbk-curtain-content.closing {
        animation: mamCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
      }
      @keyframes mamBackdropFadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes mamBackdropFadeOut {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes mamCurtainOpen {
        0% { transform: translateY(-100vh); }
        100% { transform: translateY(0); }
      }
      @keyframes mamCurtainClose {
        0% { transform: translateY(0); }
        100% { transform: translateY(-100vh); }
      }
      @media (prefers-reduced-motion: reduce) {
        .mam-curtain-backdrop, .mam-curtain-content, .mbk-curtain-content {
          animation: none !important;
        }
      }
    `}</style>
      {(isOpen || isClosing) && (
        <div
          className={`mam-curtain-backdrop ${isClosing ? "closing" : ""}`}
          onClick={handleClose}
        >
          <div
            className={`mam-curtain-content ${isClosing ? "closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff" }}
          >
            <div
              className="position-relative"
              style={{ padding: "28px 28px 32px" }}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={handleClose}
                className="position-absolute d-flex align-items-center justify-content-center border-0"
                style={{
                  top: "16px",
                  right: "16px",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(15,26,43,0.16)",
                }}
              >
                <IoClose size={28} color="#0E1421" />
              </button>

              {/* Header */}
              <h5
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#0E1421",
                  margin: "0 0 4px",
                }}
              >
                {lang?.mentorship_apply_for_mentorship_title ||
                  "Apply for mentorship"}
              </h5>
              <p
                style={{
                  color: "#5F6673",
                  fontSize: "11px",
                  fontWeight: "500",
                  margin: "0 0 22px",
                }}
              >
                {lang?.mentorship_apply_modal_subtitle ||
                  "Review your details, then choose how you'd like to pay."}
              </p>

              {/* SELECTED MENTOR */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#0E1421",
                    letterSpacing: "0.1em",
                  }}
                >
                  {lang?.mentorship_selected_mentor_label || "SELECTED MENTOR"}
                </span>
                {/* <button
            type="button"
            onClick={toggle}
            style={{
              border: "1px solid #E6E7EA",
              borderRadius: "20px",
              padding: "3px 12px",
              background: "none",
              fontSize: "10px",
              fontWeight: 500,
              color: "#0E1421",
              cursor: "pointer",
            }}
          >
            {lang?.mentorship_change_button || "Change"}
          </button> */}
              </div>

              <div
                className="d-flex align-items-start"
                style={{
                  background: "#E8FBFF",
                  borderRadius: "20px",
                  padding: "14px",
                  border: "1px solid #D7E0EA",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <img
                  src={mentor.image}
                  alt={mentor.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#111827",
                      margin: "0 0 3px",
                    }}
                  >
                    {mentor.name}
                  </p>
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {mentorSummary(mentorDisplayInfo)}
                  </p>
                </div>
              </div>

              {/* YOUR DETAILS */}
              <span
                className="d-block"
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#0E1421",
                  letterSpacing: "0.1em",
                  marginBottom: "8px",
                }}
              >
                {lang?.mentorship_your_details_label || "YOUR DETAILS"}
              </span>

              <div
                className="d-flex align-items-center"
                style={{
                  border: "1px solid #D7E0EA",
                  borderRadius: "20px",
                  padding: "14px",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <img
                  src={accountDetails.image}
                  alt={accountDetails.fullName}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#111827",
                      margin: "0 0 2px",
                    }}
                  >
                    {accountDetails.fullName}
                  </p>
                  <p
                    style={{ fontSize: "0.78rem", color: "#6b7280", margin: 0 }}
                  >
                    {accountDetails.workEmail}
                  </p>
                </div>
              </div>

              {/* UPLOAD YOUR CV — only when the mentor has this enabled in settings */}
              {showCvUpload && (
                <>
                  <span
                    className="d-block"
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#031316",
                      letterSpacing: "0.1em",
                      marginBottom: "8px",
                    }}
                  >
                    {lang?.mentorship_upload_cv_label || "UPLOAD YOUR CV"}
                  </span>

                  {cvUploadState === "idle" ? (
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById("mamCvInput")?.click()
                      }
                      style={{
                        border: `1.5px dashed ${
                          showValidation && cvMissing
                            ? "#E24C4C"
                            : dragOver
                              ? "#0ea5e9"
                              : "#d1d5db"
                        }`,
                        borderRadius: "12px",
                        padding: "28px 20px",
                        textAlign: "center",
                        marginBottom:
                          showValidation && cvMissing ? "6px" : "20px",
                        background: "#fff",
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "13px",
                          fontWeight: "400",
                          color: "#0E1421",
                        }}
                      >
                        {cvDropzoneText || (
                          <>
                            {lang?.mentorship_drag_drop_cv_prefix ||
                              "Drag & drop your CV here, or"}{" "}
                            <span
                              style={{
                                color: "#1DC1DD",
                                textDecoration: "underline",
                              }}
                            >
                              {lang?.mentorship_browse_files || "browse files"}
                            </span>
                          </>
                        )}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          fontWeight: "400",
                          color: "#5F6673",
                        }}
                      >
                        {lang?.mentorship_pdf_format || "PDF format"}
                      </p>
                      <input
                        id="mamCvInput"
                        type="file"
                        accept=".pdf"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </div>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{
                        border: `1px solid ${cvUploadState === "delayed" ? "#F4D9A8" : "#E6E7EA"}`,
                        borderRadius: "14px",
                        padding: "12px 14px",
                        marginBottom: "20px",
                        gap: "12px",
                        background: "#fff",
                      }}
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "12px", minWidth: 0 }}
                      >
                        {cvUploadState === "delayed" ? (
                          <CvDelayedIcon />
                        ) : (
                          <CvFileIcon />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <p
                            style={{
                              margin: "0 0 2px",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#0E1421",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={cvFile?.name}
                          >
                            {cvFile?.name}
                          </p>
                          <p
                            className="d-flex align-items-center"
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              color: "#5F6673",
                              gap: "6px",
                            }}
                          >
                            {cvUploadState === "uploading" && (
                              <>
                                <SpinnerIcon />
                                <span>
                                  {lang?.mentorship_pdf_format || "PDF format"}{" "}
                                  &nbsp;•&nbsp;{" "}
                                  {lang?.uploading || "Uploading…"}
                                </span>
                              </>
                            )}
                            {cvUploadState === "delayed" && (
                              <>
                                <SpinnerIcon color="#B45309" />
                                <span style={{ color: "#B45309" }}>
                                  {lang?.mentorship_cv_upload_delayed ||
                                    "Still uploading — this is taking longer than usual"}
                                </span>
                              </>
                            )}
                            {cvUploadState === "uploaded" && (
                              <span>
                                {lang?.mentorship_pdf_format || "PDF format"}{" "}
                                &nbsp;•&nbsp;{" "}
                                {formatUploadedAgo(uploadedAt, lang)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {(cvUploadState === "uploading" ||
                        cvUploadState === "delayed") && (
                        <button
                          type="button"
                          onClick={handleCancelUpload}
                          title={
                            lang?.mentorship_cancel_upload_title ||
                            "Cancel upload"
                          }
                          className="d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                            background: "#F3F4F6",
                            flexShrink: 0,
                            cursor: "pointer",
                          }}
                        >
                          <CancelIcon />
                        </button>
                      )}

                      {cvUploadState === "uploaded" && (
                        <button
                          type="button"
                          onClick={handleDeleteCv}
                          title={
                            lang?.mentorship_remove_pdf_title || "Remove PDF"
                          }
                          className="d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                            background: "#FF383C1A",
                            color: "red",
                            flexShrink: 0,
                            cursor: "pointer",
                          }}
                        >
                          <RiDeleteBin6Line />
                        </button>
                      )}
                    </div>
                  )}

                  {showValidation && cvMissing && (
                    <p
                      style={{
                        margin: "0 0 20px",
                        fontSize: "11px",
                        fontWeight: 500,
                        color: "#E24C4C",
                      }}
                    >
                      {lang?.mentorship_cv_required_error ||
                        "Please upload your CV as a PDF to continue."}
                    </p>
                  )}
                </>
              )}

              {/* PAYMENT METHOD */}
              <span
                className="d-block"
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#0E1421",
                  letterSpacing: "0.1em",
                  marginBottom: "10px",
                }}
              >
                {lang?.mentorship_payment_method_label || "PAYMENT METHOD"}
              </span>

              {/* PayPal option */}
              <div
                onClick={() => setSelectedPayment("paypal")}
                className="d-flex justify-content-between align-items-center position-relative"
                style={{
                  border: `2px solid ${selectedPayment === "paypal" ? "#061223" : "#FCD34D"}`,
                  borderRadius: "20px",
                  padding: "8px 16px",
                  marginBottom: "10px",
                  cursor: "pointer",
                  background: "#FCD34D",
                  transition: "all 0.2s",
                  overflow: "visible",
                }}
              >
                {selectedPayment === "paypal" && <SelectedBadge />}
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "0.88rem",
                    color: "#111827",
                  }}
                >
                  {lang?.mentorship_pay_with_card_paypal ||
                    "Pay with card or PayPal"}
                </span>
                <span
                  style={{
                    fontWeight: 800,
                    fontSize: "0.92rem",
                    fontStyle: "italic",
                  }}
                >
                  <span style={{ color: "#003087" }}>Pay</span>
                  <span style={{ color: "#009cde" }}>Pal</span>
                </span>
              </div>

              {/* Klarna option */}
              <div
                onClick={() => setSelectedPayment("klarna")}
                className="d-flex justify-content-between align-items-center position-relative"
                style={{
                  border: `2px solid ${selectedPayment === "klarna" ? "#061223" : "#F3B5D3"}`,
                  borderRadius: "20px",
                  padding: "8px 16px",
                  marginBottom: "18px",
                  cursor: "pointer",
                  background: "#F3B5D3",
                  transition: "all 0.2s",
                  overflow: "visible",
                }}
              >
                {selectedPayment === "klarna" && <SelectedBadge />}
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "0.88rem",
                    color: "#111827",
                  }}
                >
                  {lang?.mentorship_pay_with_klarna || "Pay with Klarna"}
                </span>
                <span
                  style={{
                    fontWeight: 800,
                    fontSize: "1rem",
                    color: "#111827",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Klarna.
                </span>
              </div>

              {/* Checkboxes */}
              <div
                className="d-flex align-items-start"
                style={{
                  gap: "10px",
                  marginBottom:
                    showValidation && checkboxMissing ? "4px" : "12px",
                }}
              >
                <div
                  onClick={() => setChecked1((prev) => !prev)}
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "4px",
                    border: `2px solid ${showValidation && checkboxMissing ? "#E24C4C" : "#00BADA"}`,
                    background: checked1 ? "#1DC1DD33" : "#fff",
                    flexShrink: 0,
                    marginTop: "1px",
                    color: "#00BADA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {checked1 && <CheckSvg />}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#5F6673",
                    lineHeight: 1.5,
                  }}
                >
                  {lang?.mentorship_accept_prefix || "I accept the"}{" "}
                  <a
                    href="https://medicalradar.es/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1DC1DD" }}
                  >
                    {lang?.mentorship_privacy_policy_lowercase ||
                      "privacy policy"}
                  </a>{" "}
                  {lang?.mentorship_accept_suffix ||
                    "and the processing of my data for the selection process."}
                </p>
              </div>

              {showValidation && checkboxMissing && (
                <p
                  style={{
                    margin: "0 0 8px 28px",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#E24C4C",
                  }}
                >
                  {lang?.mentorship_error_accept_privacy ||
                    "Please accept the privacy policy to continue."}
                </p>
              )}

              {/* Continue button — mentor's own asking price (mentorPrice,
                read off the mentor/profile API's mentor_profile.price)
                appended to the label itself. Omitted for a free mentorship. */}
              <button
                type="button"
                onClick={handleContinue}
                disabled={submittingRequest || processingPayment}
                className="d-flex align-items-center justify-content-center w-100 border-0 text-white"
                style={{
                  gap: "8px",
                  background: "#00BADA",
                  borderRadius: "30px",
                  padding: "14px",
                  fontWeight: 700,
                  fontSize: "16px",
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                  opacity: submittingRequest || processingPayment ? 0.7 : 1,
                }}
              >
                {submittingRequest
                  ? lang?.submitting || "Submitting…"
                  : processingPayment
                    ? lang?.mentorship_processing_payment || "Processing…"
                    : mentorPrice > 0
                      ? `${lang?.mentorship_continue_to_payment_button || "Continue to Payment"} - €${mentorPrice.toFixed(2)}`
                      : lang?.mentorship_continue_to_payment_button ||
                        "Continue to Payment"}
                {!submittingRequest && !processingPayment && <FaArrowRight />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mentorship Booked! — confirmation shown after Continue succeeds */}
      {(showBookedModal || isBookedClosing) && (
        <div
          className={`mam-curtain-backdrop ${isBookedClosing ? "closing" : ""}`}
          onClick={closeBookedModal}
        >
          <div
            className={`mbk-curtain-content ${isBookedClosing ? "closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff" }}
          >
            <div
              className="position-relative"
              style={{ padding: "32px 28px 28px" }}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={closeBookedModal}
                className="border-0 bg-transparent"
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  fontSize: "1.1rem",
                  color: "#6b7280",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: "4px 6px",
                }}
              >
                ✕
              </button>

              {/* Success icon */}
              <div
                className="d-flex justify-content-center"
                style={{ marginBottom: "20px" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "50%",
                    background: "#D0FFDC",
                  }}
                >
                  <svg width="54" height="54" viewBox="0 0 34 34" fill="none">
                    <path
                      d="M7 17L13.5 23.5L27 10"
                      stroke="#34C759"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h5
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#0E1421",
                  textAlign: "center",
                  margin: "0 0 8px",
                }}
              >
                {lang?.mentorship_booked_title || "Mentorship Booked!"}
              </h5>

              {/* Subtitle */}
              <p
                style={{
                  fontSize: "12px",
                  color: "#5F6673",
                  textAlign: "center",
                  fontWeight: "500",
                  margin: "0 0 22px",
                  lineHeight: 1.5,
                }}
              >
                {lang?.mentorship_booked_subtitle ||
                  "Your application has been submitted and your spot is confirmed."}
              </p>

              {mentor && (
                <div
                  className="d-flex align-items-start"
                  style={{
                    background: "#E8FBFF",
                    borderRadius: "20px",
                    border: "1px solid #D7E0EA",
                    padding: "14px",
                    gap: "13px",
                    marginBottom: "14px",
                  }}
                >
                  <img
                    src={mentor.image}
                    alt={mentor.name}
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#111827",
                        margin: "0 0 4px",
                      }}
                    >
                      {mentor.name}
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#5F6673",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {mentorSummary(mentorDisplayInfo)}
                    </p>
                  </div>
                </div>
              )}

              {/* Info box */}
              <div
                style={{
                  background: "#FFEFAD",
                  border: "1px solid #FFCC00",
                  borderRadius: "12px",
                  padding: "6px 16px",
                  marginBottom: "22px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "#0E1421",
                    margin: 0,
                    fontWeight: "500",
                    lineHeight: 1.6,
                  }}
                >
                  {lang?.mentorship_contact_info_prefix ||
                    "The mentor will contact you through the"}{" "}
                  <strong style={{ fontWeight: 700, color: "#111827" }}>
                    {lang?.mentorship_contact_info_bold ||
                      "Ophthalmology Radar in-app chat"}
                  </strong>{" "}
                  {lang?.mentorship_contact_info_suffix ||
                    "within 24 hours to schedule your mentorship session."}
                </p>
              </div>

              {/* Done button */}
              <button
                type="button"
                onClick={closeBookedModal}
                className="d-block w-100 border-0 text-white"
                style={{
                  background: "#00BADA",
                  borderRadius: "30px",
                  padding: "14px",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                }}
              >
                {lang?.mentorship_done_button || "Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PayPal Drop-in Modal */}
      <Modal
        isOpen={dropInModal}
        toggle={() => {
          setDropInModal(false);
          setProcessingPayment(false);
        }}
        size="lg"
        centered
        backdrop="static"
      >
        <ModalHeader toggle={() => { setDropInModal(false); setProcessingPayment(false); }}>
          {lang?.payment || "Payment"}
        </ModalHeader>
        <ModalBody>
          <div id="mam-dropin-container"></div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => { setDropInModal(false); setProcessingPayment(false); }}>
            {lang?.close || "Close"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Klarna Stripe Checkout Modal */}
      <Modal
        isOpen={klarnaModal}
        toggle={() => {
          setKlarnaModal(false);
          setProcessingPayment(false);
        }}
        size="lg"
        centered
        backdrop="static"
      >
        <ModalHeader toggle={() => { setKlarnaModal(false); setProcessingPayment(false); }}>
          {lang?.complete_klarna_payment || "Complete your Klarna payment"}
        </ModalHeader>
        <ModalBody>
          {sessionError && (
            <div className="alert alert-danger" role="alert">
              {sessionError}
            </div>
          )}
          {sessionPaymentStatus && <SessionStatus paymentStatus={sessionPaymentStatus} />}
          {klarnaClientSecret ? (
            <CheckoutProvider clientSecret={klarnaClientSecret}>
              <CheckoutForm
                onPaymentSuccess={(session) => {
                  // Embedded checkout finalized without a browser redirect.
                  if (session?.id) {
                    submitMentorRequest({ paymentType: "stripe", nonce: session.id });
                  }
                }}
              />
            </CheckoutProvider>
          ) : (
            <p className="text-center text-muted">
              {lang?.loading_klarna_checkout || "Loading Klarna checkout…"}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => { setKlarnaModal(false); setProcessingPayment(false); }}>
            {lang?.close || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default React.memo(MentorshipApplicationModal);

// import React, { useEffect, useRef, useState } from "react";
// import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
// import { toast } from "react-toastify";
// import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";
// import { RiDeleteBin5Line, RiDeleteBin6Line } from "react-icons/ri";
// import { FaArrowRight } from "react-icons/fa";
// import { apiAdmin, getApiErrorMessage } from "../../services/api";
// import { _imagesURL } from "../../constants/API";
// // Same PayPal (Braintree Drop-in) + Klarna (Stripe embedded Checkout Session)
// // integration used for course purchases — see
// // src/components/course-details/PaypalPaymentPage.jsx for the reference flow.
// import { fetchPaypalToken, generateNonceWithDropIn } from "../../services/paypalApi";
// import { createKlarnaCheckoutSession, getKlarnaCheckoutSessionStatus } from "../../services/stripeApi";
// import { CheckoutProvider } from "../payments/klarna-stripe/CheckoutProvider";
// import { CheckoutForm } from "../payments/klarna-stripe/CheckoutForm";
// import { SessionStatus } from "../payments/klarna-stripe/SessionStatus";

// // Same endpoint ApplyMentorShip.jsx uses — user_id is the mentor's id, and
// // the actual profile is nested one level deeper under data.data.mentor.
// async function fetchMentorProfile(userId) {
//   const res = await apiAdmin.post("user/mentor/profile", { user_id: userId });
//   return res.data;
// }

// // Used only if SplashData hasn't loaded into localStorage yet.
// const FALLBACK_ACCOUNT_DETAILS = {
//   fullName: "Dr. Jane Smith",
//   workEmail: "janesmith@hospital.com",
//   image: placeholderAvatar,
// };

// // Same SplashData.user source CreateMentorship.jsx reads from — the
// // currently logged-in doctor applying, not the mentor being applied to.
// function getAccountDetails() {
//   try {
//     const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
//     if (!user) return FALLBACK_ACCOUNT_DETAILS;
//     return {
//       fullName: user.name || [user.firstname, user.lastname].filter(Boolean).join(" ") || FALLBACK_ACCOUNT_DETAILS.fullName,
//       workEmail: user.email || FALLBACK_ACCOUNT_DETAILS.workEmail,
//       image: user.image ? `${_imagesURL}${user.image}` : FALLBACK_ACCOUNT_DETAILS.image,
//     };
//   } catch (e) {
//     return FALLBACK_ACCOUNT_DETAILS;
//   }
// }

// // How long an upload can run before we switch from the normal "Uploading…"
// // indicator to the "Upload Delay" state (per Figma: taking longer than
// // expected / stuck in progress for an extended period).
// const UPLOAD_DELAY_THRESHOLD_MS = 6000;

// function CheckSvg() {
//   return (
//     <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
//       <path
//         d="M1 4L3.5 6.5L9 1"
//         stroke="#00BADA"
//         strokeWidth="1.8"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

// // Small green circular checkmark badge — pinned to the top-right corner of
// // whichever payment option is currently selected, overlapping the option's
// // own border/edge like a "selected" pin.
// function SelectedBadge() {
//   return (
//     <span
//       className="d-flex align-items-center justify-content-center"
//       style={{
//         position: "absolute",
//         top: "-9px",
//         right: "0px",
//         width: "20px",
//         height: "20px",
//         borderRadius: "50%",
//         background: "#34C759",
//         border: "1.5px solid #0612234D",
//         boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
//       }}
//     >
//       <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
//         <path
//           d="M1 4L3.5 6.5L9 1"
//           stroke="#111"
//           strokeWidth="1.8"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </span>
//   );
// }

// // Rounded file/document icon — light green pill background, matches the
// // "uploaded" card look from the design.
// function CvFileIcon() {
//   return (
//     <div
//       style={{
//         width: "38px",
//         height: "38px",
//         borderRadius: "10px",
//         background: "#E6F7EE",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flexShrink: 0,
//       }}
//     >
//       <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
//         <path
//           d="M13 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6Z"
//           stroke="#1F9D55"
//           strokeWidth="1.7"
//           strokeLinejoin="round"
//         />
//         <path
//           d="M13 2v5a1 1 0 0 0 1 1h5"
//           stroke="#1F9D55"
//           strokeWidth="1.7"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </div>
//   );
// }

// // Same rounded slot, but amber-tinted — used for the "delayed" state so it
// // reads as a soft warning rather than a normal in-progress spinner.
// function CvDelayedIcon() {
//   return (
//     <div
//       style={{
//         width: "38px",
//         height: "38px",
//         borderRadius: "10px",
//         background: "#FEF3E2",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flexShrink: 0,
//       }}
//     >
//       <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
//         <circle cx="12" cy="12" r="9" stroke="#B45309" strokeWidth="1.7" />
//         <path
//           d="M12 7v5l3 2"
//           stroke="#B45309"
//           strokeWidth="1.7"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </div>
//   );
// }

// // Small rotating ring — used inside both the uploading and delayed cards.
// function SpinnerIcon({ color = "#5F6673", size = 14 }) {
//   return (
//     <>
//       <style>{`@keyframes cvUploadSpin{to{transform:rotate(360deg)}}`}</style>
//       <span
//         style={{
//           display: "inline-block",
//           width: `${size}px`,
//           height: `${size}px`,
//           border: `2px solid ${color}33`,
//           borderTopColor: color,
//           borderRadius: "50%",
//           animation: "cvUploadSpin 0.7s linear infinite",
//           flexShrink: 0,
//         }}
//       />
//     </>
//   );
// }

// function TrashIcon() {
//   return (
//     <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
//       <path
//         d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"
//         stroke="#E24C4C"
//         strokeWidth="1.7"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

// function CancelIcon() {
//   return (
//     <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
//       <path
//         d="M6 6l12 12M18 6 6 18"
//         stroke="#5F6673"
//         strokeWidth="1.8"
//         strokeLinecap="round"
//       />
//     </svg>
//   );
// }

// // "Uploaded just now" / "Uploaded 4 min ago" / "Uploaded 2 hours ago" —
// // recomputed live off uploadedAt so it never goes stale while the modal
// // stays open.
// function formatUploadedAgo(uploadedAt) {
//   if (!uploadedAt) return "";
//   const seconds = Math.floor((Date.now() - uploadedAt) / 1000);
//   if (seconds < 45) return "Uploaded just now";
//   const minutes = Math.floor(seconds / 60);
//   if (minutes < 60) return `Uploaded ${minutes} min${minutes > 1 ? "s" : ""} ago`;
//   const hours = Math.floor(minutes / 60);
//   if (hours < 24) return `Uploaded ${hours} hour${hours > 1 ? "s" : ""} ago`;
//   const days = Math.floor(hours / 24);
//   return `Uploaded ${days} day${days > 1 ? "s" : ""} ago`;
// }

// function mentorSummary(mentor) {
//   if (!mentor) return "";
//   return [mentor.specialty, mentor.institution, mentor.institutionSub].filter(Boolean).join(" · ");
// }

// function MentorshipApplicationModal({ isOpen, toggle, mentor, onSubmitted }) {
//   const [checked1, setChecked1] = useState(true);
//   const [checked2, setChecked2] = useState(false);
//   const [selectedPayment, setSelectedPayment] = useState("paypal");
//   const [dragOver, setDragOver] = useState(false);
//   const [mentorProfileData, setMentorProfileData] = useState(null);
//   const [submittingRequest, setSubmittingRequest] = useState(false);
//   // Payment step — mirrors PaypalPaymentPage.jsx's Braintree Drop-in /
//   // Stripe embedded Checkout Session flow.
//   const [processingPayment, setProcessingPayment] = useState(false);
//   const [dropInModal, setDropInModal] = useState(false);
//   const [klarnaModal, setKlarnaModal] = useState(false);
//   const [klarnaClientSecret, setKlarnaClientSecret] = useState(null);
//   const [sessionError, setSessionError] = useState(null);
//   const [sessionPaymentStatus, setSessionPaymentStatus] = useState(null);
//   const accountDetails = getAccountDetails();

//   useEffect(() => {
//     if (!isOpen || !mentor?.id) {
//       setMentorProfileData(null);
//       return;
//     }
//     let cancelled = false;
//     fetchMentorProfile(mentor.id)
//       .then((data) => {
//         if (!cancelled && data?.status) setMentorProfileData(data.data?.mentor || null);
//       })
//       .catch(() => {});
//     return () => {
//       cancelled = true;
//     };
//   }, [isOpen, mentor?.id]);

//   // Real "Receive CV with Mentorship Requests" setting from this specific
//   // mentor's profile — the CV section only shows when they've turned it on.
//   const mentorProfile = mentorProfileData?.mentor_profile;
//   const showCvUpload = mentorProfile?.is_receive_data === 1;
//   const cvDropzoneText = showCvUpload && mentorProfile?.receive_data_title
//     ? mentorProfile.receive_data_title
//     : null;

//   const [showBookedModal, setShowBookedModal] = useState(false);

//   const [cvFile, setCvFile] = useState(null);
//   // "idle" -> no file yet, dropzone visible
//   // "uploading" -> file picked, upload in flight, within normal time window
//   // "delayed" -> still uploading past UPLOAD_DELAY_THRESHOLD_MS
//   // "uploaded" -> upload finished, file card with delete/replace shown
//   const [cvUploadState, setCvUploadState] = useState("idle");
//   const [uploadedAt, setUploadedAt] = useState(null);
//   // Forces a re-render every 30s so "Uploaded just now" keeps advancing
//   // ("1 min ago", "2 mins ago"...) without needing any other state change.
//   const [, forceTick] = useState(0);

//   const delayTimerRef = useRef(null);
//   const uploadTimerRef = useRef(null);
//   const tickIntervalRef = useRef(null);

//   const clearUploadTimers = () => {
//     if (delayTimerRef.current) {
//       clearTimeout(delayTimerRef.current);
//       delayTimerRef.current = null;
//     }
//     if (uploadTimerRef.current) {
//       clearTimeout(uploadTimerRef.current);
//       uploadTimerRef.current = null;
//     }
//   };

//   useEffect(() => {
//     return () => clearUploadTimers();
//   }, []);

//   // Keep the relative "Uploaded X ago" label fresh while a file sits uploaded.
//   useEffect(() => {
//     if (cvUploadState !== "uploaded") return;
//     tickIntervalRef.current = setInterval(() => forceTick((n) => n + 1), 30000);
//     return () => clearInterval(tickIntervalRef.current);
//   }, [cvUploadState]);

//   // Klarna's checkout can redirect the browser away and back (e.g. its own
//   // financing approval step) — this picks the flow back up on return via
//   // ?session_id= in the URL, same pattern as PaypalPaymentPage.jsx. Must sit
//   // above the `if (!mentor) return null` below — hooks can't be conditional.
//   useEffect(() => {
//     const params = new URLSearchParams(window.location.search);
//     const sessionId = params.get("session_id");
//     const pending = localStorage.getItem("mam_pending_request");
//     if (!sessionId || !pending || !mentor?.id) return;

//     setProcessingPayment(true);
//     getKlarnaCheckoutSessionStatus(sessionId)
//       .then((res) => {
//         const sessionData = res?.data?.data ?? res?.data;
//         const paymentStatus = sessionData?.payment_status ?? res?.payment_status;
//         setSessionPaymentStatus(paymentStatus);
//         if (paymentStatus === "paid") {
//           return submitMentorRequest({ paymentType: "stripe", nonce: sessionId });
//         }
//         throw new Error(`Payment was not completed (status: ${paymentStatus ?? "unknown"}).`);
//       })
//       .catch((err) => {
//         console.error("Klarna session verification error:", err);
//         setSessionError(err?.response?.data?.message || err.message || "Failed to verify payment.");
//         setProcessingPayment(false);
//       });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [mentor?.id]);

//   // Placeholder for the real upload call — swap this out for the actual
//   // API request. Kept as its own function so wiring up a real endpoint
//   // later is a one-line change; everything else (delay detection, state
//   // transitions, cleanup) stays the same.
//   const uploadCvFile = (file) => {
//     return new Promise((resolve) => {
//       const duration = 1500 + Math.random() * 2000;
//       uploadTimerRef.current = setTimeout(resolve, duration);
//     });
//   };

//   const startUpload = (file) => {
//     if (!file) return;
//     clearUploadTimers();
//     setCvFile(file);
//     setCvUploadState("uploading");
//     setUploadedAt(null);

//     // Flip to the "delayed" visual if the upload is still going after the
//     // threshold — this only changes what's shown, the upload itself keeps
//     // running underneath.
//     delayTimerRef.current = setTimeout(() => {
//       setCvUploadState((prev) => (prev === "uploading" ? "delayed" : prev));
//     }, UPLOAD_DELAY_THRESHOLD_MS);

//     uploadCvFile(file).then(() => {
//       if (delayTimerRef.current) {
//         clearTimeout(delayTimerRef.current);
//         delayTimerRef.current = null;
//       }
//       setCvUploadState("uploaded");
//       setUploadedAt(Date.now());
//     });
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files?.[0] || null;
//     // Reset the input so selecting the same filename again after a delete
//     // still fires onChange.
//     e.target.value = "";
//     if (file) startUpload(file);
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     setDragOver(false);
//     if (cvUploadState !== "idle") return; // a file is already in flight/uploaded
//     const file = e.dataTransfer.files?.[0];
//     if (file) startUpload(file);
//   };

//   // Cancels an in-flight upload (uploading/delayed) — back to the empty
//   // dropzone, nothing kept.
//   const handleCancelUpload = () => {
//     clearUploadTimers();
//     setCvFile(null);
//     setCvUploadState("idle");
//     setUploadedAt(null);
//   };

//   // Removes a completed upload — this is the only way back to the empty
//   // dropzone once a file has finished uploading, so a new PDF can't be
//   // picked while one is already attached.
//   const handleDeleteCv = () => {
//     clearUploadTimers();
//     setCvFile(null);
//     setCvUploadState("idle");
//     setUploadedAt(null);
//   };

//   if (!mentor) return null;

//   // The mentor's own asking price, set on their application form
//   // (CreateMentorship.jsx's "price" field) — same field, just not shown
//   // anywhere on the mentee side until now.
//   const mentorPrice = Number(mentorProfile?.price ?? mentor.mentor_profile?.price ?? 0) || 0;

//   // --- Previous behavior (kept for reference, not removed) ---
//   // "Continue to Payment" used to submit the mentor/request straight away,
//   // with no real payment step in between despite the PayPal/Klarna cards
//   // already being selectable above. Replaced below by a real payment step
//   // (Braintree Drop-in for PayPal, Stripe embedded Checkout for Klarna) that
//   // automatically fires this same request once payment succeeds.
//   //
//   // const handleContinue = async () => {
//   //   if (!checked1) {
//   //     toast.error("Please accept the privacy policy to continue.");
//   //     return;
//   //   }
//   //   setSubmittingRequest(true);
//   //   try {
//   //     const mentorProfileId = mentorProfile?.id ?? mentor.mentor_profile?.id;
//   //     const fd = new FormData();
//   //     fd.append("mentor_id", mentor.id);
//   //     fd.append("mentorship_type", "One-To-One");
//   //     if (cvUploadState === "uploaded" && cvFile) {
//   //       fd.append("data", cvFile);
//   //     }
//   //     const res = await apiAdmin.post("user/mentor/request", fd, {
//   //       headers: { "Content-Type": "multipart/form-data" },
//   //     });
//   //     if (res.data?.status) {
//   //       onSubmitted?.(mentor);
//   //       toggle();
//   //       setShowBookedModal(true);
//   //     } else {
//   //       toast.error(getApiErrorMessage(res.data, "Couldn't submit your request. Please try again."));
//   //     }
//   //   } catch (err) {
//   //     toast.error(getApiErrorMessage(err.response?.data, "Couldn't submit your request. Please try again."));
//   //   } finally {
//   //     setSubmittingRequest(false);
//   //   }
//   // };

//   // Actually submits the mentor/request — same endpoint/payload as before,
//   // now called automatically right after a payment succeeds (PayPal nonce
//   // or Klarna/Stripe session id attached as paymentMeta).
//   const submitMentorRequest = async (paymentMeta) => {
//     setSubmittingRequest(true);
//     try {
//       const fd = new FormData();
//       fd.append("mentor_id", mentor.id);
//       fd.append("mentorship_type", "One-To-One");
//       if (cvUploadState === "uploaded" && cvFile) {
//         fd.append("data", cvFile);
//       }
//       if (paymentMeta?.paymentType) fd.append("payment_type", paymentMeta.paymentType);
//       if (paymentMeta?.nonce) fd.append("nonce", paymentMeta.nonce);
//       const res = await apiAdmin.post("user/mentor/reqwuest", fd, {
//         headers: { "Content-Type": "multipart/form-data" },
//       });
//       if (res.data?.status) {
//         localStorage.removeItem("mam_pending_request");
//         onSubmitted?.(mentor);
//         setDropInModal(false);
//         setKlarnaModal(false);
//         toggle();
//         setShowBookedModal(true);
//       } else {
//         toast.error(getApiErrorMessage(res.data, "Couldn't submit your request. Please try again."));
//       }
//     } catch (err) {
//       toast.error(getApiErrorMessage(err.response?.data, "Couldn't submit your request. Please try again."));
//     } finally {
//       setSubmittingRequest(false);
//       setProcessingPayment(false);
//     }
//   };

//   // ── PayPal / Card — Braintree Drop-in, same as PaypalPaymentPage.jsx ──────
//   const handlePaypalPayment = async () => {
//     setProcessingPayment(true);
//     try {
//       if (mentorPrice > 0) {
//         const userToken = await fetchPaypalToken();
//         setDropInModal(true);
//         const nonce = await generateNonceWithDropIn(userToken, "mam-dropin-container", mentorPrice.toFixed(2));
//         await submitMentorRequest({ paymentType: "paypal", nonce });
//       } else {
//         // Free mentorship (no price set) — nothing to tokenize.
//         await submitMentorRequest({ paymentType: "paypal" });
//       }
//     } catch (err) {
//       console.error("PayPal payment error:", err);
//       toast.error("Couldn't process PayPal payment. Please try again.");
//       setDropInModal(false);
//       setProcessingPayment(false);
//     }
//   };

//   // ── Klarna — Stripe embedded Checkout Session, same as PaypalPaymentPage.jsx ──
//   const handleKlarnaPayment = async () => {
//     setProcessingPayment(true);
//     setSessionError(null);
//     try {
//       // Persisted so the redirect-return effect below can finish this
//       // request even after Klarna sends the browser away and back.
//       localStorage.setItem(
//         "mam_pending_request",
//         JSON.stringify({ mentorId: mentor.id })
//       );
//       const amount = Math.round(mentorPrice * 100);
//       const response = await createKlarnaCheckoutSession({
//         courseId: mentor.id,
//         amount,
//         title: `Mentorship with ${mentor.name}`,
//       });
//       const clientSecret = response?.data?.client_secret ?? response?.client_secret;
//       if (clientSecret) {
//         setKlarnaClientSecret(clientSecret);
//         setKlarnaModal(true);
//       } else {
//         throw new Error("Could not initialize Klarna checkout.");
//       }
//     } catch (err) {
//       console.error("Klarna payment error:", err);
//       toast.error("Couldn't start Klarna checkout. Please try again.");
//       setProcessingPayment(false);
//     }
//   };

//   const handleContinue = () => {
//     if (!checked1) {
//       toast.error("Please accept the privacy policy to continue.");
//       return;
//     }
//     if (selectedPayment === "klarna") {
//       handleKlarnaPayment();
//     } else {
//       handlePaypalPayment();
//     }
//   };

//   const closeBookedModal = () => {
//     setShowBookedModal(false);
//   };

//   return (
//     <>
//     <Modal
//       isOpen={isOpen}
//       toggle={toggle}
//       centered
//       className="mentorship-page mam-modal-dialog"
//       contentClassName="mam-modal-content"
//     >
//       <style>{`
//         .mam-modal-content {
//           border: none;
//           border-radius: 18px;
//           overflow: hidden;
//           box-shadow: 0 20px 60px rgba(0,0,0,0.2);
//         }
//         .mam-modal-dialog {
//           max-width: 540px;
//         }
//       `}</style>
//       <ModalBody className="position-relative" style={{ padding: "28px 28px 32px" }}>
//         {/* Close button */}
//         <button
//           type="button"
//           onClick={toggle}
//           className="border-0 bg-transparent"
//           style={{
//             position: "absolute",
//             top: "16px",
//             right: "16px",
//             fontSize: "1.1rem",
//             cursor: "pointer",
//             color: "#6b7280",
//             lineHeight: 1,
//             padding: "4px",
//           }}
//         >
//           ✕
//         </button>

//         {/* Header */}
//         <h5 style={{ fontWeight: 700, fontSize: "20px", color: "#0E1421", margin: "0 0 4px" }}>
//           Apply for mentorship
//         </h5>
//         <p style={{ color: "#5F6673", fontSize: "11px", fontWeight:"500", margin: "0 0 22px" }}>
//           Review your details, then choose how you'd like to pay.
//         </p>

//         {/* SELECTED MENTOR */}
//         <div className="d-flex justify-content-between align-items-center mb-2">
//           <span style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em" }}>
//             SELECTED MENTOR
//           </span>
//           <button
//             type="button"
//             onClick={toggle}
//             style={{
//               border: "1px solid #E6E7EA",
//               borderRadius: "20px",
//               padding: "3px 12px",
//               background: "none",
//               fontSize: "10px",
//               fontWeight: 500,
//               color: "#0E1421",
//               cursor: "pointer",
//             }}
//           >
//             Change
//           </button>
//         </div>

//         <div
//           className="d-flex align-items-start"
//           style={{
//             background: "#E8FBFF",
//             borderRadius: "20px",
//             padding: "14px",
//             border:"1px solid #D7E0EA",
//             gap: "12px",
//             marginBottom: "20px",
//           }}
//         >
//           <img
//             src={mentor.image}
//             alt={mentor.name}
//             style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//           />
//           <div>
//             <p style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", margin: "0 0 3px" }}>
//               {mentor.name}
//             </p>
//             <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
//               {mentorSummary(mentor)}
//             </p>
//           </div>
//         </div>

//         {/* YOUR DETAILS */}
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em", marginBottom: "8px" }}
//         >
//           YOUR DETAILS
//         </span>

//         <div
//           className="d-flex align-items-center"
//           style={{ border: "1px solid #D7E0EA", borderRadius: "20px", padding: "14px", gap: "12px", marginBottom: "20px" }}
//         >
//           <img
//             src={accountDetails.image}
//             alt={accountDetails.fullName}
//             style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//           />
//           <div>
//             <p style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", margin: "0 0 2px" }}>
//               {accountDetails.fullName}
//             </p>
//             <p style={{ fontSize: "0.78rem", color: "#6b7280", margin: 0 }}>
//               {accountDetails.workEmail}
//             </p>
//           </div>
//         </div>

//         {/* UPLOAD YOUR CV — only when the mentor has this enabled in settings */}
//         {showCvUpload && (
//           <>
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#031316", letterSpacing: "0.1em", marginBottom: "8px" }}
//         >
//           UPLOAD YOUR CV
//         </span>

//         {cvUploadState === "idle" ? (
//           <div
//             onDragOver={(e) => {
//               e.preventDefault();
//               setDragOver(true);
//             }}
//             onDragLeave={() => setDragOver(false)}
//             onDrop={handleDrop}
//             onClick={() => document.getElementById("mamCvInput")?.click()}
//             style={{
//               border: `1.5px dashed ${dragOver ? "#0ea5e9" : "#d1d5db"}`,
//               borderRadius: "12px",
//               padding: "28px 20px",
//               textAlign: "center",
//               marginBottom: "20px",
//               background: "#fff",
//               transition: "all 0.2s",
//               cursor: "pointer",
//             }}
//           >
//             <p style={{ margin: "0 0 4px", fontSize: "13px", fontWeight:"400", color: "#0E1421" }}>
//               {cvDropzoneText || (
//                 <>
//                   Drag &amp; drop your CV here, or{" "}
//                   <span style={{ color: "#1DC1DD", textDecoration: "underline" }}>browse files</span>
//                 </>
//               )}
//             </p>
//             <p style={{ margin: 0, fontSize: "11px", fontWeight:"400", color: "#5F6673" }}>PDF format</p>
//             <input
//               id="mamCvInput"
//               type="file"
//               accept=".pdf"
//               style={{ display: "none" }}
//               onChange={handleFileChange}
//             />
//           </div>
//         ) : (
//           <div
//             className="d-flex align-items-center justify-content-between"
//             style={{
//               border: `1px solid ${cvUploadState === "delayed" ? "#F4D9A8" : "#E6E7EA"}`,
//               borderRadius: "14px",
//               padding: "12px 14px",
//               marginBottom: "20px",
//               gap: "12px",
//               background: "#fff",
//             }}
//           >
//             <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0 }}>
//               {cvUploadState === "delayed" ? <CvDelayedIcon /> : <CvFileIcon />}
//               <div style={{ minWidth: 0 }}>
//                 <p
//                   style={{
//                     margin: "0 0 2px",
//                     fontSize: "13px",
//                     fontWeight: 600,
//                     color: "#0E1421",
//                     whiteSpace: "nowrap",
//                     overflow: "hidden",
//                     textOverflow: "ellipsis",
//                   }}
//                   title={cvFile?.name}
//                 >
//                   {cvFile?.name}
//                 </p>
//                 <p
//                   className="d-flex align-items-center"
//                   style={{ margin: 0, fontSize: "11px", color: "#5F6673", gap: "6px" }}
//                 >
//                   {cvUploadState === "uploading" && (
//                     <>
//                       <SpinnerIcon />
//                       <span>PDF format &nbsp;•&nbsp; Uploading…</span>
//                     </>
//                   )}
//                   {cvUploadState === "delayed" && (
//                     <>
//                       <SpinnerIcon color="#B45309" />
//                       <span style={{ color: "#B45309" }}>
//                         Still uploading — this is taking longer than usual
//                       </span>
//                     </>
//                   )}
//                   {cvUploadState === "uploaded" && (
//                     <span>PDF format &nbsp;•&nbsp; {formatUploadedAgo(uploadedAt)}</span>
//                   )}
//                 </p>
//               </div>
//             </div>

//             {(cvUploadState === "uploading" || cvUploadState === "delayed") && (
//               <button
//                 type="button"
//                 onClick={handleCancelUpload}
//                 title="Cancel upload"
//                 className="d-flex align-items-center justify-content-center border-0"
//                 style={{
//                   width: "34px",
//                   height: "34px",
//                   borderRadius: "50%",
//                   background: "#F3F4F6",
//                   flexShrink: 0,
//                   cursor: "pointer",
//                 }}
//               >
//                 <CancelIcon />
//               </button>
//             )}

//             {cvUploadState === "uploaded" && (
//               <button
//                 type="button"
//                 onClick={handleDeleteCv}
//                 title="Remove PDF"
//                 className="d-flex align-items-center justify-content-center border-0"
//                 style={{
//                   width: "34px",
//                   height: "34px",
//                   borderRadius: "50%",
//                   background: "#FF383C1A",
//                   color:"red",
//                   flexShrink: 0,
//                   cursor: "pointer",
//                 }}
//               >
//               <RiDeleteBin6Line />

//               </button>
//             )}
//           </div>
//         )}
//           </>
//         )}

//         {/* PAYMENT METHOD */}
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em", marginBottom: "10px" }}
//         >
//           PAYMENT METHOD
//         </span>

//         {/* PayPal option */}
// <div
//   onClick={() => setSelectedPayment("paypal")}
//   className="d-flex justify-content-between align-items-center position-relative"
//   style={{
//     border: `2px solid ${selectedPayment === "paypal" ? "#061223" : "#FCD34D"}`,
//     borderRadius: "20px",
//     padding: "8px 16px",
//     marginBottom: "10px",
//     cursor: "pointer",
//     background: "#FCD34D",
//     transition: "all 0.2s",
//     overflow: "visible",
//   }}
// >
//   {selectedPayment === "paypal" && <SelectedBadge />}
//   <span style={{ fontWeight: 500, fontSize: "0.88rem", color: "#111827" }}>
//     Pay with card or PayPal
//   </span>
//   <span style={{ fontWeight: 800, fontSize: "0.92rem", fontStyle: "italic" }}>
//     <span style={{ color: "#003087" }}>Pay</span>
//     <span style={{ color: "#009cde" }}>Pal</span>
//   </span>
// </div>

// {/* Klarna option */}
// <div
//   onClick={() => setSelectedPayment("klarna")}
//   className="d-flex justify-content-between align-items-center position-relative"
//   style={{
//     border: `2px solid ${selectedPayment === "klarna" ? "#061223" : "#F3B5D3"}`,
//     borderRadius: "20px",
//     padding: "8px 16px",
//     marginBottom: "18px",
//     cursor: "pointer",
//     background: "#F3B5D3",
//     transition: "all 0.2s",
//     overflow: "visible",
//   }}
// >
//   {selectedPayment === "klarna" && <SelectedBadge />}
//   <span style={{ fontWeight: 500, fontSize: "0.88rem", color: "#111827" }}>
//     Pay with Klarna
//   </span>
//   <span style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", letterSpacing: "-0.02em" }}>
//     Klarna.
//   </span>
// </div>

//         {/* Checkboxes */}
//         <div className="d-flex align-items-start" style={{ gap: "10px", marginBottom: "12px" }}>
//           <div
//             onClick={() => setChecked1((prev) => !prev)}
//             style={{
//               width: "18px",
//               height: "18px",
//               borderRadius: "4px",
//               border: `2px solid ${checked1 ? "#00BADA" : "#00BADA"}`,
//               background: checked1 ? "#1DC1DD33" : "#fff",
//               flexShrink: 0,
//               marginTop: "1px",
//               color:"#00BADA",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               cursor: "pointer",
//               transition: "all 0.15s",
//             }}
//           >
//             {checked1 && <CheckSvg />}
//           </div>
//           <p style={{ margin: 0, fontSize: "12px", color: "#5F6673", lineHeight: 1.5 }}>
//             I accept the{" "}
//             <a
//               href="https://medicalradar.es/privacy-policy"
//               target="_blank"
//               rel="noopener noreferrer"
//               style={{ color: "#1DC1DD", }}
//             >
//               privacy policy
//             </a>{" "}
//             and the processing of my data for the selection process.
//           </p>
//         </div>

//         {/* Continue button */}
//         <button
//           type="button"
//           onClick={handleContinue}
//           disabled={submittingRequest || processingPayment}
//           className="d-flex align-items-center justify-content-center w-100 border-0 text-white"
//           style={{
//             gap: "8px",
//             background: "#00BADA",
//             borderRadius: "30px",
//             padding: "14px",
//             fontWeight: 700,
//             fontSize: "16px",
//             cursor: "pointer",
//             letterSpacing: "0.01em",
//             opacity: submittingRequest || processingPayment ? 0.7 : 1,
//           }}
//         >
//           {submittingRequest
//             ? "Submitting…"
//             : processingPayment
//             ? "Processing payment…"
//             : "Continue to Payment"}
//         {!submittingRequest && !processingPayment && <FaArrowRight />}

//         </button>
//       </ModalBody>
//     </Modal>

//     {/* PayPal Drop-in Modal */}
//     <Modal
//       isOpen={dropInModal}
//       toggle={() => {
//         setDropInModal(false);
//         setProcessingPayment(false);
//       }}
//       size="lg"
//       centered
//       backdrop="static"
//     >
//       <ModalHeader toggle={() => setDropInModal(false)}>Payment</ModalHeader>
//       <ModalBody>
//         <div id="mam-dropin-container"></div>
//       </ModalBody>
//       <ModalFooter>
//         <Button
//           color="secondary"
//           onClick={() => {
//             setDropInModal(false);
//             setProcessingPayment(false);
//           }}
//         >
//           Close
//         </Button>
//       </ModalFooter>
//     </Modal>

//     {/* Klarna Stripe Checkout Modal */}
//     <Modal isOpen={klarnaModal} toggle={() => setKlarnaModal(false)} size="lg" centered backdrop="static">
//       <ModalHeader toggle={() => setKlarnaModal(false)}>Complete your Klarna payment</ModalHeader>
//       <ModalBody>
//         {sessionError && <div className="alert alert-danger mb-3">{sessionError}</div>}
//         {sessionPaymentStatus && <SessionStatus paymentStatus={sessionPaymentStatus} />}
//         {klarnaClientSecret ? (
//           <CheckoutProvider clientSecret={klarnaClientSecret}>
//             <CheckoutForm
//               onPaymentSuccess={(session) => {
//                 // Embedded checkout finalized without a browser redirect.
//                 if (session?.id) {
//                   submitMentorRequest({ paymentType: "stripe", nonce: session.id });
//                 }
//               }}
//             />
//           </CheckoutProvider>
//         ) : (
//           <p className="text-center text-muted">Loading Klarna checkout…</p>
//         )}
//       </ModalBody>
//       <ModalFooter>
//         <Button color="secondary" onClick={() => setKlarnaModal(false)}>
//           Close
//         </Button>
//       </ModalFooter>
//     </Modal>

//     {/* Mentorship Booked! — confirmation shown after Continue succeeds */}
//     <Modal
//       isOpen={showBookedModal}
//       toggle={closeBookedModal}
//       centered
//       className="mentorship-page mbk-modal-dialog"
//       contentClassName="mbk-modal-content"
//     >
//       <style>{`
//         .mbk-modal-content {
//           border: none;
//           border-radius: 20px;
//           overflow: hidden;
//           box-shadow: 0 24px 64px rgba(0,0,0,0.25);
//         }
//         .mbk-modal-dialog {
//           max-width: 520px;
//         }
//       `}</style>
//       <ModalBody className="position-relative" style={{ padding: "32px 28px 28px" }}>
//         {/* Close button */}
//         <button
//           type="button"
//           onClick={closeBookedModal}
//           className="border-0 bg-transparent"
//           style={{
//             position: "absolute",
//             top: "16px",
//             right: "16px",
//             fontSize: "1.1rem",
//             color: "#6b7280",
//             cursor: "pointer",
//             lineHeight: 1,
//             padding: "4px 6px",
//           }}
//         >
//           ✕
//         </button>

//         {/* Success icon */}
//         <div className="d-flex justify-content-center" style={{ marginBottom: "20px" }}>
//           <div
//             className="d-flex align-items-center justify-content-center"
//             style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#D0FFDC" }}
//           >
//             <svg width="54" height="54" viewBox="0 0 34 34" fill="none">
//               <path
//                 d="M7 17L13.5 23.5L27 10"
//                 stroke="#34C759"
//                 strokeWidth="4"
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//               />
//             </svg>
//           </div>
//         </div>

//         {/* Title */}
//         <h5
//           style={{
//             fontWeight: 700,
//             fontSize: "20px",
//             color: "#0E1421",
//             textAlign: "center",
//             margin: "0 0 8px",
//           }}
//         >
//           Mentorship Booked!
//         </h5>

//         {/* Subtitle */}
//         <p
//           style={{
//             fontSize: "12px",
//             color: "#5F6673",
//             textAlign: "center",
//             fontWeight:"500",
//             margin: "0 0 22px",
//             lineHeight: 1.5,
//           }}
//         >
//           Your application has been submitted and your spot is confirmed.
//         </p>

//         {mentor && (
//           <div
//             className="d-flex align-items-start"
//             style={{
//               background: "#E8FBFF",
//               borderRadius: "20px",
//               border:"1px solid #D7E0EA",
//               padding: "14px",
//               gap: "13px",
//               marginBottom: "14px",
//             }}
//           >
//             <img
//               src={mentor.image}
//               alt={mentor.name}
//               style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//             />
//             <div>
//               <p style={{ fontWeight: 500, fontSize: "14px", color: "#111827", margin: "0 0 4px" }}>
//                 {mentor.name}
//               </p>
//               <p style={{ fontSize: "12px", color: "#5F6673", margin: 0, lineHeight: 1.5 }}>
//                 {mentorSummary(mentor)}
//               </p>
//             </div>
//           </div>
//         )}

//         {/* Info box */}
//         <div
//           style={{
//             background: "#FFEFAD",
//             border: "1px solid #FFCC00",
//             borderRadius: "12px",
//             padding: "6px 16px",
//             marginBottom: "22px",
//             textAlign: "center",
//           }}
//         >
//           <p style={{ fontSize: "12px", color: "#0E1421", margin: 0, fontWeight:"500", lineHeight: 1.6 }}>
//             The mentor will contact you through the{" "}
//             <strong style={{ fontWeight: 700, color: "#111827" }}>
//               Ophthalmology Radar in-app chat
//             </strong>{" "}
//             within 24 hours to schedule your mentorship session.
//           </p>
//         </div>

//         {/* Done button */}
//         <button
//           type="button"
//           onClick={closeBookedModal}
//           className="d-block w-100 border-0 text-white"
//           style={{
//             background: "#00BADA",
//             borderRadius: "30px",
//             padding: "14px",
//             fontWeight: 600,
//             fontSize: "0.95rem",
//             cursor: "pointer",
//             letterSpacing: "0.01em",
//           }}
//         >
//           Done
//         </button>
//       </ModalBody>
//     </Modal>
//     </>
//   );
// }

// export default React.memo(MentorshipApplicationModal);
